'use client';

/**
 * Root-layout error boundary — the last line of defence.
 *
 * This component catches errors that happen inside the root <html>/<body>
 * layout itself (e.g. a crash in the ThemeProvider, PostHog wrapper, etc.)
 * where the full layout is unavailable.
 *
 * Because the root layout may be broken, this file must define a complete
 * standalone HTML document.  It deliberately uses no external deps (no
 * Tailwind, no motion) to stay self-contained and avoid cascading failures.
 *
 * IMPORTANT: Never display error.message or error.stack here.
 */

import { useEffect } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[global-error-boundary]', error.digest ?? 'no-digest');
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Critical Error — ImpactOT</title>
        <style>{`
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            min-height: 100vh;
            background: #0a0a0a;
            color: #ededed;
            font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2rem;
          }
          .container {
            max-width: 28rem;
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
          }
          .badge {
            font-size: 0.7rem;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #888;
          }
          .badge span { color: #D97706; }
          h1 { font-size: 1.75rem; font-weight: 800; line-height: 1.2; }
          h1 span { color: #D97706; }
          p { color: #888; font-size: 0.875rem; line-height: 1.6; }
          .digest { font-family: ui-monospace, monospace; font-size: 0.7rem; color: #555; }
          button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            background: #D97706;
            color: #fff;
            border: none;
            padding: 0.75rem 1.75rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s;
          }
          button:hover { background: #B45309; }
        `}</style>
      </head>
      <body>
        <div className="container">
          <p className="badge">
            ERROR <span>/</span> CRITICAL
          </p>
          <h1>
            A critical error<br />
            <span>occurred</span>
          </h1>
          <p>
            ImpactOT encountered an unexpected problem.
            Please try again — if this persists, contact support.
          </p>
          {error.digest && (
            <p className="digest">Reference: {error.digest}</p>
          )}
          <div>
            <button onClick={reset}>Try again</button>
          </div>
        </div>
      </body>
    </html>
  );
}
